import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { CustomizationInputType, type Customization } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { keyFrom } from '../api/codes';
import {
  emptyCustomizationForm,
  fromCustomization,
  newOptionRow,
  toCustomizationCreate,
  toCustomizationUpdate,
} from '../api/customization-mappers';
import {
  useCreateCustomization,
  useUpdateCustomization,
} from '../hooks/use-customization-mutations';
import { useSizeSets } from '../hooks/use-size-sets';
import {
  customizationFormSchema,
  UPLOAD_CONTENT_TYPES,
  type CustomizationFormInput,
  type CustomizationFormValues,
} from '../schemas/customization-form-schema';

export interface CustomizationDialogProps {
  /** The row being edited; null creates a new one. */
  customization: Customization | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type CustomizationForm = UseFormReturn<CustomizationFormInput, unknown, CustomizationFormValues>;

/** Inline options: label and stored value per row; a saved row's value is fixed. */
function OptionRows({ form }: { form: CustomizationForm }) {
  const { t } = useTranslation('dictionary');
  const rows = useFieldArray({ control: form.control, name: 'options' });
  const [draft, setDraft] = useState('');
  const listError = form.formState.errors.options?.message;

  const add = () => {
    const label = draft.trim();
    if (!label) return;
    const taken = form.getValues('options').map((row) => row.value);
    rows.append(newOptionRow(label, taken));
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-2 sm:col-span-2">
      <div>
        <span className="text-sm font-medium">{t('fields.options')}</span>
        <p className="text-muted-foreground text-xs">{t('fields.optionsHint')}</p>
      </div>
      {rows.fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('fields.optionsEmpty')}</p>
      ) : (
        <ol className="divide-y rounded-md border" aria-label={t('fields.options')}>
          {rows.fields.map((row, index) => {
            const n = index + 1;
            return (
              <li
                key={row.id}
                aria-label={t('options.row', { n })}
                className="grid grid-cols-[3.5rem_1fr_1fr_7rem_2rem] items-start gap-2 px-3 py-2"
              >
                <div className="flex gap-0.5 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('options.moveUp', { n })}
                    disabled={index === 0}
                    onClick={() => rows.move(index, index - 1)}
                  >
                    <ArrowUp aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('options.moveDown', { n })}
                    disabled={index === rows.fields.length - 1}
                    onClick={() => rows.move(index, index + 1)}
                  >
                    <ArrowDown aria-hidden />
                  </Button>
                </div>
                <FormField
                  control={form.control}
                  name={`options.${index}.label`}
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <Input
                          {...field}
                          maxLength={120}
                          aria-label={t('options.labelFor', { n })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`options.${index}.value`}
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <Input
                          {...field}
                          maxLength={120}
                          disabled={row.existing}
                          className="font-mono text-[13px]"
                          aria-label={t('options.valueFor', { n })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`options.${index}.priceDelta`}
                  render={({ field, fieldState }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <MoneyInput
                          currency={BASE_CURRENCY}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="0.00"
                          aria-invalid={fieldState.invalid}
                          aria-label={t('options.priceFor', { n })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground"
                  aria-label={t('options.remove', { n })}
                  onClick={() => rows.remove(index)}
                >
                  <X aria-hidden />
                </Button>
              </li>
            );
          })}
        </ol>
      )}
      {typeof listError === 'string' ? (
        <p role="alert" className="text-destructive text-xs">
          {t(listError)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder={t('fields.optionsPlaceholder')}
          aria-label={t('options.newLabel')}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" disabled={!draft.trim()} onClick={add}>
          <Plus aria-hidden />
          {t('options.add')}
        </Button>
      </div>
    </div>
  );
}

function DialogBody({
  customization,
  onClose,
}: {
  customization: Customization | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['dictionary', 'common']);
  const message = useErrorMessage();
  const acceptId = useId();
  const anyId = useId();
  const create = useCreateCustomization();
  const update = useUpdateCustomization();
  const sizeSets = useSizeSets();
  const isPending = create.isPending || update.isPending;
  const form = useForm<CustomizationFormInput, unknown, CustomizationFormValues>({
    resolver: useI18nResolver(customizationFormSchema),
    defaultValues: customization ? fromCustomization(customization) : emptyCustomizationForm(),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const inputType = form.watch('inputType');
  const optionsSource = form.watch('optionsSource');
  const acceptAny = form.watch('acceptAny');
  // The key follows the label until it is edited by hand.
  const keyTouched = useRef(false);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (customization) {
        await update.mutateAsync({ id: customization.id, body: toCustomizationUpdate(values) });
      } else {
        await create.mutateAsync(toCustomizationCreate(values));
      }
      onClose();
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert" className="sm:col-span-2">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:fields.label')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!customization && !keyTouched.current) {
                      form.setValue('key', keyFrom(e.target.value));
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:fields.key')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={customization !== null}
                  className="font-mono text-[13px]"
                  onChange={(e) => {
                    keyTouched.current = true;
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>
                {customization ? t('dictionary:fields.keyFixed') : t('dictionary:fields.keyHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="inputType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:fields.inputType')}</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={customization !== null}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(CustomizationInputType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`dictionary:inputType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {customization
                  ? t('dictionary:fields.inputTypeFixed')
                  : t(`dictionary:inputTypeHint.${field.value}`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="isRequired"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between gap-4 self-end rounded-md border p-3">
              <FormLabel>{t('dictionary:fields.isRequired')}</FormLabel>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
        {inputType === 'select' ? (
          <>
            <FormField
              control={form.control}
              name="optionsSource"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>{t('dictionary:fields.optionsSource')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="grid gap-2 sm:grid-cols-2"
                    >
                      {(['inline', 'size_set'] as const).map((source) => (
                        <div
                          key={source}
                          className="flex items-start gap-2.5 rounded-md border p-3"
                        >
                          <RadioGroupItem
                            value={source}
                            id={`options-source-${source}`}
                            className="mt-0.5"
                          />
                          <div className="grid gap-0.5">
                            <Label htmlFor={`options-source-${source}`}>
                              {t(`dictionary:optionsSource.${source}`)}
                            </Label>
                            <span className="text-muted-foreground text-xs">
                              {t(`dictionary:optionsSourceHint.${source}`)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {optionsSource === 'size_set' ? (
              <FormField
                control={form.control}
                name="sizeSetId"
                render={({ field, fieldState }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('dictionary:fields.sizeSet')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full" aria-invalid={fieldState.invalid}>
                          <SelectValue placeholder={t('dictionary:fields.sizeSetPlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(sizeSets.data ?? []).map((set) => (
                          <SelectItem key={set.id} value={set.id}>
                            {set.name}
                            <span className="text-muted-foreground">
                              {' · '}
                              {t('dictionary:sizeSets.valueCount', { count: set.values.length })}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {sizeSets.data?.length === 0
                        ? t('dictionary:fields.sizeSetNone')
                        : t('dictionary:fields.sizeSetHint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <OptionRows form={form} />
            )}
          </>
        ) : null}
        {inputType === 'text' ? (
          <>
            <FormField
              control={form.control}
              name="maxLength"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dictionary:fields.maxLength')}</FormLabel>
                  <FormControl>
                    <Input type="number" inputMode="numeric" min={1} max={2000} {...field} />
                  </FormControl>
                  <FormDescription>{t('dictionary:fields.maxLengthHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pattern"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dictionary:fields.pattern')}</FormLabel>
                  <FormControl>
                    <Input className="font-mono text-[13px]" {...field} />
                  </FormControl>
                  <FormDescription>{t('dictionary:fields.patternHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : null}
        {inputType === 'file' ? (
          <FormField
            control={form.control}
            name="accept"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel id={acceptId}>{t('dictionary:fields.accept')}</FormLabel>
                <div className="flex flex-col gap-3 rounded-md border p-3">
                  <FormField
                    control={form.control}
                    name="acceptAny"
                    render={({ field: any }) => (
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id={anyId}
                          className="mt-0.5"
                          checked={any.value}
                          onCheckedChange={(next) => any.onChange(next === true)}
                        />
                        <Label
                          htmlFor={anyId}
                          className="flex flex-col items-start gap-0.5 font-normal"
                        >
                          <span>{t('dictionary:fields.acceptAny')}</span>
                          <span className="text-muted-foreground text-xs">
                            {t('dictionary:fields.acceptAnyHint')}
                          </span>
                        </Label>
                      </div>
                    )}
                  />
                  <div
                    role="group"
                    aria-labelledby={acceptId}
                    className="grid gap-2 border-t pt-3 sm:grid-cols-3"
                  >
                    {UPLOAD_CONTENT_TYPES.map((type) => {
                      const id = `${acceptId}-${type}`;
                      const checked = acceptAny || field.value.includes(type);
                      return (
                        <div key={type} className="flex items-start gap-2">
                          <Checkbox
                            id={id}
                            className="mt-0.5"
                            checked={checked}
                            disabled={acceptAny}
                            onCheckedChange={(next) =>
                              field.onChange(
                                next === true
                                  ? UPLOAD_CONTENT_TYPES.filter(
                                      (candidate) =>
                                        candidate === type || field.value.includes(candidate),
                                    )
                                  : field.value.filter((candidate) => candidate !== type),
                              )
                            }
                          />
                          <Label
                            htmlFor={id}
                            className="flex flex-col items-start gap-0.5 font-normal"
                          >
                            <span>{t(`dictionary:contentType.${type}`)}</span>
                            <code className="text-muted-foreground text-xs">{type}</code>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <FormDescription>{t('dictionary:fields.acceptHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          control={form.control}
          name="priceDelta"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('dictionary:fields.priceDelta')}</FormLabel>
              <FormControl>
                <MoneyInput
                  currency={BASE_CURRENCY}
                  value={field.value}
                  onChange={(next) =>
                    field.onChange(next ?? { amount: 0, currency: BASE_CURRENCY })
                  }
                  onBlur={field.onBlur}
                  aria-invalid={fieldState.invalid}
                />
              </FormControl>
              <FormDescription>{t('dictionary:fields.priceDeltaHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="affectsLeadTimeDays"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dictionary:fields.leadTime')}</FormLabel>
              <FormControl>
                <Input type="number" inputMode="numeric" min={0} {...field} />
              </FormControl>
              <FormDescription>{t('dictionary:fields.leadTimeHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="helpText"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('dictionary:fields.helpText')}</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
              <FormDescription>{t('dictionary:fields.helpTextHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter className="sm:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending
              ? t('common:actions.saving')
              : customization
                ? t('common:actions.save')
                : t('dictionary:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Create or edit one customization; key and input type are fixed once saved. */
export function CustomizationDialog({
  customization,
  open,
  onOpenChange,
}: CustomizationDialogProps) {
  const { t } = useTranslation('dictionary');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {customization ? t('edit.title', { label: customization.label }) : t('create.title')}
          </DialogTitle>
          <DialogDescription>
            {customization ? t('edit.description') : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <DialogBody
            key={customization?.updatedAt ?? 'new'}
            customization={customization}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
